/**
 * GENERATED by /docs-writer from docs/transforge-multi-cloud.md — DO NOT EDIT BY HAND.
 * Edit the markdown and run `/docs-writer update docs/transforge-multi-cloud.md`.
 *
 * source-sha: 6679948195bad3f25e42df3ed10117c13de0ce86
 * sources (from the markdown's "Sources" section):
 *   - Live production scan, 2026-10-12: kubectl on the Oracle Cloud cluster and both DigitalOcean clusters,
 *     the Oracle Cloud DNS steering policy and its health monitor, DNS lookups and HTTPS probes of the three
 *     hosts (all read-only)
 *   - TransForge hourly stats rollup, table cloud_stats, 2026-09-07 to 2026-10-12
 *   - os-transforge at origin/main b64cf43d: cloud.ts (queue names, cloud tag), app.ts (rejectIfWrongCloud,
 *     returned hosts), assembly-routes.ts (status route), docs/runbooks/multicloud-queues.md, the production
 *     manifests for both clouds
 *   - os-transforge merge request 205, docs/architecture/L1-assembly-id-convention.md (accepted 2026-10-02,
 *     open on 2026-10-12)
 *   - os-transforge docs/clients/README.md (the client contract)
 *   - conversation, 2026-10-01 to 2026-10-12 (measured wrong-cloud status read)
 */
import { Link } from 'react-router';
import { DocSequence } from '../components/DocSequence';
import { DocsToc } from '../components/DocsToc';

const TOC = [
  { id: 'what-the-two-clouds-share', label: 'What the two clouds share' },
  { id: 'what-each-cloud-runs', label: 'What each cloud runs' },
  { id: 'which-host-answers-from-which-cloud', label: 'Which host answers from which cloud' },
  { id: 'how-the-global-name-picks-a-cloud', label: 'How the global name picks a cloud' },
  { id: 'why-a-client-must-follow-the-returned-urls', label: 'Why a client must follow the returned URLs' },
  { id: 'what-an-assembly-id-says-about-its-cloud', label: 'What an assembly ID says about its cloud' },
  { id: 'how-much-traffic-oracle-cloud-takes', label: 'How much traffic Oracle Cloud takes' },
];

// Matrix cells: the mark is drawn in CSS, the word is for screen readers.
const YES = (
  <span className="doc-yes">
    <span className="doc-vh">allowed</span>
  </span>
);
const NO = (
  <span className="doc-no">
    <span className="doc-vh">blocked</span>
  </span>
);

export function TransforgeMultiCloudDocsPage() {
  return (
    <>
      <div className="page doc" id="top">
        <p className="doc-eyebrow">Media · Concept</p>
        <h1 className="doc-title">TransForge on two clouds</h1>
        <p className="doc-lede">
          <Link to="/docs/transforge">TransForge</Link> runs as two copies, one on DigitalOcean and one on Oracle
          Cloud. They share one database and one queue server, and each keeps its own files. An assembly therefore{' '}
          <strong>belongs to the cloud that created it</strong>. A global name sends 90% of new requests to
          DigitalOcean and 10% to Oracle Cloud. Only one client uses that name on 12 October 2026, so Oracle Cloud
          has created 17 assemblies in total.
        </p>
        <p className="doc-meta">
          Updated <time dateTime="2026-10-12">12 Oct 2026</time>
        </p>

        <h2 className="doc-h2" id="what-the-two-clouds-share">What the two clouds share</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Thing</th>
                <th>Shared or separate</th>
                <th>What that means</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Accounts, keys and templates</strong></td>
                <td>Shared</td>
                <td>One MongoDB serves both clouds. A key or a template works on both at once.</td>
              </tr>
              <tr>
                <td><strong>Assembly records</strong></td>
                <td>Shared</td>
                <td>
                  Each record carries the cloud that created it: <code>do</code> or <code>oci</code>.
                </td>
              </tr>
              <tr>
                <td><strong>Job queues</strong></td>
                <td>Separate, on one Redis</td>
                <td>
                  DigitalOcean uses names such as <code>robot-video-encode</code>. Oracle Cloud adds{' '}
                  <code>-oci</code> to every name. A worker takes jobs from its own cloud only.
                </td>
              </tr>
              <tr>
                <td><strong>Working files and results</strong></td>
                <td>Separate</td>
                <td>Each cloud has its own file share. Nothing copies files between them.</td>
              </tr>
              <tr>
                <td><strong>Cleanup and archive</strong></td>
                <td>Separate</td>
                <td>
                  Each cloud cleans up the assemblies it created. DigitalOcean also owns the older assemblies that
                  carry no cloud.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          An assembly never moves. The cloud that accepts the create request runs every step and holds every file
          until cleanup removes them.
        </p>

        <h2 className="doc-h2" id="what-each-cloud-runs">What each cloud runs</h2>
        <p className="doc-p">In the table, ✓ means the cloud runs it and – means it does not.</p>
        <div className="doc-table doc-matrix">
          <table>
            <thead>
              <tr>
                <th scope="col">Part</th>
                <th scope="col">DigitalOcean</th>
                <th scope="col">Oracle Cloud</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">API gateway</th>
                <td>{YES}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Assembly service</th>
                <td>{YES}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Robot workers, all four groups</th>
                <td>{YES}</td>
                <td>{YES}</td>
              </tr>
              <tr>
                <th scope="row">Worker autoscaling</th>
                <td>{YES}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row">Console API</th>
                <td>{YES}</td>
                <td>{NO}</td>
              </tr>
              <tr>
                <th scope="row">Companion</th>
                <td>{YES}</td>
                <td>{NO}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">Oracle Cloud is the smaller copy. Its replica counts are set by hand.</p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Workload</th>
                <th className="doc-num">DigitalOcean</th>
                <th className="doc-num">Oracle Cloud</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>api-gateway</code></td>
                <td className="doc-num">8</td>
                <td className="doc-num">2</td>
              </tr>
              <tr>
                <td><code>assembly-service</code></td>
                <td className="doc-num">4</td>
                <td className="doc-num">2</td>
              </tr>
              <tr>
                <td><code>robot-workers</code></td>
                <td className="doc-num">8</td>
                <td className="doc-num">3</td>
              </tr>
              <tr>
                <td><code>robot-workers-document-thumbs</code></td>
                <td className="doc-num">7</td>
                <td className="doc-num">2</td>
              </tr>
              <tr>
                <td><code>robot-workers-playwright</code></td>
                <td className="doc-num">2</td>
                <td className="doc-num">1</td>
              </tr>
              <tr>
                <td><code>robot-workers-video-encode</code></td>
                <td className="doc-num">1</td>
                <td className="doc-num">1</td>
              </tr>
              <tr>
                <td>Nodes</td>
                <td className="doc-num">16</td>
                <td className="doc-num">5</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          DigitalOcean nodes are x86. Oracle Cloud nodes are ARM, so the pipeline builds every image for both CPU
          types.
        </p>

        <h2 className="doc-h2" id="which-host-answers-from-which-cloud">Which host answers from which cloud</h2>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Host</th>
                <th>Answers from</th>
                <th>Use it for</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><code>transforge-api.optisigns.com</code></td>
                <td>DigitalOcean only</td>
                <td>Any request. It is the host that ten of the eleven clients call.</td>
              </tr>
              <tr>
                <td><code>transforge-api-oci.optisigns.com</code></td>
                <td>Oracle Cloud only</td>
                <td>Requests for an assembly that Oracle Cloud created.</td>
              </tr>
              <tr>
                <td><code>transforge-api-global.optisigns.com</code></td>
                <td>Either cloud</td>
                <td>The create request only.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="doc-p">
          All three hosts answer <code>/healthz</code>, and all three allow browser requests from any origin.
        </p>

        <h2 className="doc-h2" id="how-the-global-name-picks-a-cloud">How the global name picks a cloud</h2>
        <DocSequence
          caption="A create through the global name that lands on Oracle Cloud"
          participants={[
            { id: 'Client', label: 'Client' },
            { id: 'Dns', label: 'Global name', note: 'DNS steering' },
            { id: 'Oci', label: 'Oracle Cloud API' },
            { id: 'Do', label: 'DigitalOcean API' },
          ]}
          steps={[
            { from: 'Client', to: 'Dns', text: 'Resolve the global name' },
            { kind: 'reply', from: 'Dns', to: 'Client', text: 'Answer one healthy cloud' },
            { from: 'Client', to: 'Oci', text: 'Create the assembly' },
            { kind: 'reply', from: 'Oci', to: 'Client', text: 'Return URLs on the Oracle host' },
            { from: 'Client', to: 'Oci', text: 'Upload and read on those URLs' },
            { kind: 'note', over: ['Do'], text: 'Holds no files for this assembly' },
          ]}
        />
        <p className="doc-p">
          The global name is a DNS record with a steering policy. The policy applies three rules in order.
        </p>
        <ol className="doc-list">
          <li>
            It drops a cloud that fails the health check. The check requests <code>/healthz</code> on each cloud
            every 30 seconds, from two places.
          </li>
          <li>It picks one of the remaining clouds by weight: DigitalOcean 90, Oracle Cloud 10.</li>
          <li>It returns that one address, with a lifetime of 60 seconds.</li>
        </ol>
        <p className="doc-p">
          After a cloud fails its health check, a resolver can keep the old answer for up to 60 seconds. Assemblies
          that the failed cloud already holds stay on it, and the other cloud cannot serve them.
        </p>

        <h2 className="doc-h2" id="why-a-client-must-follow-the-returned-urls">
          Why a client must follow the returned URLs
        </h2>
        <p className="doc-p">
          The create response carries a status URL and an upload URL. Both use the host of the cloud that created
          the assembly. A client is safe when every later request uses those URLs.{' '}
          <Link to="/docs/transforge-clients">Clients</Link> lists which clients do.
        </p>
        <p className="doc-p">
          A client that builds a URL from the global name reaches the other cloud some of the time. The two kinds of
          request fail differently.
        </p>
        <div className="doc-table">
          <table>
            <thead>
              <tr>
                <th>Request on the wrong cloud</th>
                <th>What the client gets</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Upload</td>
                <td>
                  An error: HTTP 409 with the code <code>WRONG_CLOUD</code>.
                </td>
              </tr>
              <tr>
                <td>Status read</td>
                <td>
                  HTTP 200 with the real status and <strong>no results</strong>.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <aside className="doc-note doc-note--warn">
          <span className="doc-note-label">Warning</span>
          <p>
            A status read on the wrong cloud looks like a finished assembly that produced nothing. The status comes
            from the shared database, and the results come from files that only the other cloud holds. Check the{' '}
            <code>region</code> field in the answer: it names the cloud that created the assembly.
          </p>
        </aside>
        <p className="doc-p">
          The same empty answer appears on the right cloud after cleanup has removed the files.{' '}
          <Link to="/docs/transforge-operations">Deploy and operate</Link> gives the retention times.
        </p>

        <h2 className="doc-h2" id="what-an-assembly-id-says-about-its-cloud">What an assembly ID says about its cloud</h2>
        <p className="doc-p">
          An assembly ID says nothing about its cloud today. Every ID is 32 random hex characters.
        </p>
        <p className="doc-p">
          A convention accepted on 2 October 2026 changes that for new IDs, and it is <strong>not implemented</strong>.
          The first character names the cloud: <code>1</code> for DigitalOcean and <code>2</code> for Oracle Cloud.
          The last two characters are a checksum, so that an old random ID is not mistaken for a tagged one. A system
          that kept only an ID can then pick the right host before it calls. The shared database stays the authority
          on which cloud owns an assembly.
        </p>

        <h2 className="doc-h2" id="how-much-traffic-oracle-cloud-takes">How much traffic Oracle Cloud takes</h2>
        <p className="doc-p">
          Oracle Cloud takes almost none. The stats count 17 assemblies that it created, and the last one was on 1
          October 2026. Every one of the 671,527 assemblies from 5 to 11 October ran on DigitalOcean.
        </p>
        <p className="doc-p">
          The 90 to 10 weight applies only to requests that arrive on the global name. Giphy is the only client on
          it, and Giphy created 3 assemblies in those seven days. The Oracle Cloud share grows only when a larger
          client moves to the global name.
        </p>

        <aside className="doc-next">
          <div>
            <h2 className="doc-next-title">Moving a client to the global name?</h2>
            <p>Check that it is safe first, and what must change if it is not.</p>
          </div>
          <Link className="btn" to="/docs/transforge-clients">
            Read Clients
          </Link>
        </aside>

        <p className="doc-back">
          <a href="#top">Back to top ↑</a>
        </p>
      </div>
      <DocsToc items={TOC} />
    </>
  );
}
